import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { withMediaSipContext } from '../../contexts/MediaSipContext';
import { styled } from '@mui/material/styles';
import MediaView from './MediaView/MediaView';
import Fab from '@mui/material/Fab';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CallEndIcon from '@mui/icons-material/CallEnd';
import MicNone from '@mui/icons-material/MicNone';
import MicOff from '@mui/icons-material/MicOff';
// import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
// import avatarPlaceholder from '../../avatar-placeholder.png';
import * as sessionStates from '../../sessionStates';

const ButtonStyled = styled(Fab)(({ theme }) => ({
	margin: theme.spacing(2)
}));

const ButtonList = styled('div')({
	position: 'absolute',
	bottom: '1vh',
	transform: 'translate(-50%, 0)',
	left: '50%',
	width: '100vw',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexWrap: 'wrap',
	overflow: 'hidden',
	overflowX: 'auto',
	overflowY: 'hidden',
	'&::-webkit-scrollbar': {
		display: 'none'
	}
});

const SessionViewStyled = styled('div')({
	position: 'absolute',
	width: '100%',
	height: '100%',
	textAlign: 'center'
});

const LocalVideoContainer = styled('div')({
	position: 'absolute',
	top: '20px',
	right: '20px',
	width: '200px',
	height: '150px',
	borderRadius: '12px',
	overflow: 'hidden',
	backgroundColor: '#000',
	border: '2px solid rgba(255, 255, 255, 0.3)',
	boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
	zIndex: 10,
	'& video': {
		width: '100%',
		height: '100%',
		objectFit: 'cover'
	}
});

// const LocalVideoPlaceholder = styled('div')({
// 	width: '100%',
// 	height: '100%',
// 	display: 'flex',
// 	alignItems: 'center',
// 	justifyContent: 'center',
// 	backgroundColor: '#1a1a1a',
// 	color: '#fff',
// 	fontSize: '14px'
// });

// const LocalAvatarStyled = styled('img')({
// 	width: '100%',
// 	height: '100%',
// 	objectFit: 'contain',
// 	backgroundColor: '#1a1a1a'
//   });  

const SessionView = (props) => {
	const {
		sipCaller
	} = props;

	const currentSession = useSelector(state => state.userStatus.currentSession);
	const session = useSelector(state => state.sessions[currentSession]);
	const videoEnabled = useSelector(state => state.user.videoEnabled);
	const audioEnabled = useSelector(state => state.user.audioEnabled);
	// const avatarImageUri = useSelector(state => state.user.avatarImageUri);
	
	// State for toggling local video preview visibility
	const [showLocalPreview, setShowLocalPreview] = useState(true);
	const [, setForceUpdate] = useState(0);

	const localVideoRef = useRef(null);
	const localStream = session?.localStream;

	const localVideoTracks = localStream?.getVideoTracks?.() || [];

	// For LOCAL preview: rely on enabled + live (do NOT use muted)
	const hasLocalVideo =
	localVideoTracks.length > 0 &&
	localVideoTracks.some(t => t.readyState === "live" && t.enabled);

	// Detect avatar/canvas track
	const isAvatarTrack = localVideoTracks.some(t =>
	((t.label || "").toLowerCase().includes("canvas")) ||
	((t.label || "").toLowerCase().includes("capture"))
	);
    
	const [isEnding, setIsEnding] = useState(false);

	useEffect(() => {
	// reset when session changes
	setIsEnding(false);
	}, [currentSession]);

	// Monitor remote video tracks to detect when remote user toggles camera
	// This ensures component re-renders automatically when remote camera state changes
	useEffect(() => {
		if (!session?.remoteStream) return;

		const remoteStream = session.remoteStream;
		const tracks = remoteStream.getVideoTracks();
		
		if (tracks.length === 0) return;

		const checkRemoteCameraState = () => {
			setForceUpdate(prev => prev + 1);
		};

		// Listen to track events
		tracks.forEach(track => {
			track.addEventListener('mute', checkRemoteCameraState);
			track.addEventListener('unmute', checkRemoteCameraState);
			track.addEventListener('ended', checkRemoteCameraState);
		});

		// Also check periodically in case events don't fire
		const interval = setInterval(checkRemoteCameraState, 500);

		return () => {
			clearInterval(interval);
			tracks.forEach(track => {
				track.removeEventListener('mute', checkRemoteCameraState);
				track.removeEventListener('unmute', checkRemoteCameraState);
				track.removeEventListener('ended', checkRemoteCameraState);
			});
		};
	}, [session?.remoteStream]);

	// Don't show avatar if session is terminated (user left the call)
	const isSessionTerminated = session && session.sessionState === sessionStates.TERMINATED;
	
	// Show avatar when remote user turns off camera:
	// 1. Session is NOT terminated (user hasn't left)
	// 2. AND remote stream exists
	// 3. AND there's no valid video (no tracks OR all tracks disabled/ended/muted)
	// const remoteVideoTracks = session?.remoteStream?.getVideoTracks() || [];

	// Remote camera OFF usually shows as track.muted === true (receiver side).
	// track.enabled is not reliable on receiver.
	// const hasRemoteVideo =
	// remoteVideoTracks.length > 0 &&
	// remoteVideoTracks.some(track =>
	// 	track.readyState === 'live' && !track.muted
	// );
	
	// const shouldShowAvatar = !isSessionTerminated && session && session.remoteStream && !hasRemoteVideo;

	// Update local video preview
	useEffect(() => {
		const localVideo = localVideoRef.current;
		if (!localVideo) return;

		if (localStream && videoEnabled) {
			const videoTracks = localStream.getVideoTracks();
			const hasLocalVideo = videoTracks.length > 0 && 
				videoTracks.some(track => track.enabled && track.readyState === 'live');

			if (hasLocalVideo) {
				if (localVideo.srcObject !== localStream) {
					localVideo.srcObject = localStream;
				}
				localVideo.play().catch(err => {
					console.warn('Local video play failed:', err);
				});
			} else {
				if (localVideo.srcObject) {
					localVideo.pause();
					localVideo.srcObject = null;
				}
			}
		} else {
			if (localVideo.srcObject) {
				localVideo.pause();
				localVideo.srcObject = null;
			}
		}
	}, [localStream, videoEnabled]);

	if (session && session.remoteStream) {
		return (
			<SessionViewStyled>
				{/* Show remote video stream (other caller), with avatar placeholder when remote video is disabled or stopped */}
				{/* Hide avatar when session is terminated (user left) */}
				<MediaView
					mediaStream={session.remoteStream}
					showAvatar={false}                                // never show avatar when ended/ending
					showBlank={isEnding || isSessionTerminated} 	  // show blank when ending/terminated
					isLocal={false}                                   // this is remote video
					isMainScreen={true}                               // this is the main screen - show loader here
				/>

				{/* Local video preview (your own camera) - toggleable */}
				{/* {localStream && showLocalPreview && (
				<LocalVideoContainer>
					<MediaView
					mediaStream={localStream}
					showAvatar={!videoEnabled || isAvatarTrack || !hasLocalVideo}
					showBlank={isEnding || isSessionTerminated}
					isLocal={true}                                    // this is local video
					isMainScreen={false}                              // this is preview - don't show loader here
					/>
				</LocalVideoContainer>
				)} */}

				<ButtonList>
					<ButtonStyled
						aria-label='Mute/Unmute Microphone'
						size='medium'
						onClick={() => sipCaller.toggleMyMedia(session.sipSession, 'audio')}
					>
						{!audioEnabled ?
							<MicOff />
							:
							<MicNone />
						}
					</ButtonStyled>
					<ButtonStyled
						color='error'
						aria-label='Hang up'
						size='large'
						onClick={() => {
							setIsEnding(true);
							sipCaller.terminate(session.sipSession);
						}}
					>
						<CallEndIcon />
					</ButtonStyled>
					<ButtonStyled
						aria-label='Enable/Disable Camera'
						size='medium'
						color={videoEnabled ? 'default' : 'inherit'}
						onClick={() => sipCaller.toggleMyMedia(session.sipSession, 'video')}
					>
						{!videoEnabled ?
							<VideocamOffIcon />
							:
							<VideocamIcon />
						}
					</ButtonStyled>
					{/* <ButtonStyled
						aria-label='Toggle Local Video Preview'
						size='medium'
						color={showLocalPreview ? 'default' : 'inherit'}
						onClick={() => setShowLocalPreview(prev => !prev)}
					>
						{showLocalPreview ?
							<VisibilityIcon />
							:
							<VisibilityOffIcon />
						}
					</ButtonStyled> */}

				</ButtonList>
			</SessionViewStyled>
		);
	}
	else {
		return null;
	}
};

SessionView.propTypes =
{
	sipCaller: PropTypes.object.isRequired
};

export default withMediaSipContext(SessionView);